import { useState, useEffect } from "react";

type ScriptStatus = "loading" | "loaded" | "error";
const scriptStatusMap = new Map<string, ScriptStatus>();

export function useScript(src: string): [boolean, boolean] {
  const [state, setState] = useState(() => {
    if (!src) {
      return { loaded: false, error: false };
    }
    const status = scriptStatusMap.get(src);
    return {
      loaded: status === "loaded",
      error: status === "error",
    };
  });

  useEffect(() => {
    if (!src) {
      return () => {};
    }
    const status = scriptStatusMap.get(src);

    if (status === "loaded") {
      setState({ loaded: true, error: false });
      return () => {};
    }

    if (status === "error") {
      setState({ loaded: false, error: true });
      return () => {};
    }

    const onScriptLoad = () => {
      scriptStatusMap.set(src, "loaded");
      setState({ loaded: true, error: false });
    };

    const onScriptError = () => {
      scriptStatusMap.set(src, "error");
      document.querySelector(`script[src="${src}"]`)?.remove();
      setState({ loaded: false, error: true });
    };

    if (status === "loading") {
      // Another mount already created the <script> — attach to it and wait
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        existing.addEventListener("load", onScriptLoad);
        existing.addEventListener("error", onScriptError);
        return () => {
          existing.removeEventListener("load", onScriptLoad);
          existing.removeEventListener("error", onScriptError);
        };
      }
      return () => {};
    }

    scriptStatusMap.set(src, "loading");
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.addEventListener("load", onScriptLoad);
    script.addEventListener("error", onScriptError);
    document.body.appendChild(script);

    return () => {
      script.removeEventListener("load", onScriptLoad);
      script.removeEventListener("error", onScriptError);
    };
  }, [src]);

  return [state.loaded, state.error];
}
